// skeleton — default skin module
// Token disposition: uses --run-skeleton-* with --run-color-surface-* fallbacks
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: block;
    --_sk-color:     var(--run-skeleton-color,     var(--run-color-surface-subtle, #e0e0e0));
    --_sk-highlight: var(--run-skeleton-highlight,  var(--run-color-surface-default, #f5f5f5));
    --_sk-radius:    var(--run-skeleton-radius,     4px);
    --_sk-duration:  var(--run-skeleton-duration,  var(--run-motion-semantic-attention-duration, 1.5s));
  }

  /* ── Base block ─────────────────────────────────────────────────────────── */
  .sk-block {
    display: block;
    background: var(--_sk-color);
    border-radius: var(--_sk-radius);
    overflow: hidden;
    position: relative;
  }

  .sk-block-circular {
    border-radius: 50%;
  }

  /* ── Multi-line wrapper ─────────────────────────────────────────────────── */
  .sk-lines {
    display: flex;
    flex-direction: column;
    gap: 0.5em;
  }

  /* ── Pulse animation ────────────────────────────────────────────────────── */
  @keyframes sk-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.4; }
  }

  .sk-effect-pulse {
    animation: sk-pulse var(--_sk-duration) ease-in-out infinite;
  }

  /* ── Shimmer animation ──────────────────────────────────────────────────── */
  @keyframes sk-shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }

  .sk-effect-shimmer {
    background: linear-gradient(
      90deg,
      var(--_sk-color)     25%,
      var(--_sk-highlight) 50%,
      var(--_sk-color)     75%
    );
    background-size: 400% 100%;
    animation: sk-shimmer var(--_sk-duration) linear infinite;
  }

  /* ── Reduced-motion: disable all animation ──────────────────────────────── */
  :host([data-reduced-motion]) .sk-effect-pulse,
  :host([data-reduced-motion]) .sk-effect-shimmer {
    animation: none;
    background: var(--_sk-color);
    background-image: none;
  }
`;

/**
 * Render the skeleton layout into host.shadowRoot.
 *
 * Rules implemented:
 * - vi-01: all output carries aria-hidden="true"
 * - vi-03: last line in multi-line text at 80% width
 * - vi-04: circular with only width → height equals width (resolved by host._resolvedHeight)
 * - vi-05: data-reduced-motion suppresses animations (CSS handles this)
 * - vi-06: invalid enum values fall back to valid defaults (handled by property setters)
 * - vi-07: wireframe never animates (handled by wireframe skin, not this one)
 *
 * @param {import('../run-skeleton.js').RunSkeleton} host
 */
export function render(host) {
  const variant  = host.variant  || 'text';
  const effect   = host.effect   || 'pulse';
  const width    = host.width    || '100%';
  const height   = host._resolvedHeight;
  const lines    = host.lines    || 1;

  const effectClass   = `sk-effect-${effect}`;
  const circularClass = variant === 'circular' ? ' sk-block-circular' : '';

  if (variant === 'text' && lines > 1) {
    // vi-03: last line at 80% width
    const lineEls = Array.from({ length: lines }, (_, i) => {
      const isLast = i === lines - 1;
      const w      = isLast ? `calc(${width} * 0.8)` : width;
      return `<span class="sk-block ${effectClass}" style="width:${w};height:${height};" aria-hidden="true"></span>`;
    });
    host.shadowRoot.innerHTML = `
      <div class="sk-lines" aria-hidden="true" part="base">${lineEls.join('')}</div>
    `.trim();
  } else {
    host.shadowRoot.innerHTML = `
      <span class="sk-block${circularClass} ${effectClass}"
            style="width:${width};height:${height};"
            aria-hidden="true"
            part="base"></span>
    `.trim();
  }
}
